import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Auth } from 'aws-amplify'

interface DropdownItem {
  label: string
  to: string
}

interface NavDropdownProps {
  label: string
  items: DropdownItem[]
  isMobile?: boolean
}

const NavDropdown: React.FC<NavDropdownProps> = ({ label, items, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (isMobile) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 text-left text-indigo-900 hover:bg-gray-200 rounded flex items-center justify-between"
        >
          {label}
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="pl-4 py-1">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-3 py-2 text-indigo-900 hover:bg-gray-200 rounded text-sm"
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 text-indigo-900 hover:bg-gray-200 rounded inline-flex items-center gap-1"
      >
        {label}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 pt-1">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px]">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-4 py-2 text-indigo-900 hover:bg-gray-100 text-sm"
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const NavBar: React.FC = () => {
  const [isUserLoggedIn, setUserLoggedIn] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        await Auth.currentAuthenticatedUser()
        setUserLoggedIn(true)
      } catch (err) {
        setUserLoggedIn(false)
      }
    }

    (async () => {
      await checkAuth()
    })().catch(err => {
      console.log(err)
    })
  }, [])

  const handleMenuToggle = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.preventDefault()
    setIsMenuOpen(!isMenuOpen)
  }

  const donationsItems: DropdownItem[] = [
    { label: 'Campaigns', to: '/campaigns' },
    { label: 'Donation Types', to: '/donations' },
    { label: 'Transactions', to: '/transactions' },
    { label: 'Gift Aid Donors', to: '/gift-aid' },
    { label: 'Analytics', to: '/analytics' },
  ]

  const communicationsItems: DropdownItem[] = [
    { label: 'Send Notifications', to: '/notifications' },
    { label: 'Manage News Feed', to: '/events' },
  ]

  const settingsItems: DropdownItem[] = [
    { label: 'Feature Flags', to: '/feature-flags' },
  ]

  return (
    <nav className="bg-gray-50 border-b border-gray-200">
      <div className="flex items-center justify-between p-3 flex-wrap">
        {/* Logo */}
        <Link to="/" className="p-2 inline-flex items-center hover:opacity-80">
          <img
            src="/masjid_logo.png"
            alt="Ross Street Masjid"
            width={40}
            height={40}
          />
          <span className="text-sm font-medium px-2 text-gray-700">Ross Street Masjid Admin</span>
        </Link>

        {/* Mobile menu button */}
        <button
          className="text-gray-600 inline-flex p-3 hover:bg-gray-200 rounded lg:hidden ml-auto"
          onClick={handleMenuToggle}
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:items-center lg:gap-1">
          {isUserLoggedIn && (
            <>
              <Link
                to="/"
                className="px-3 py-2 text-indigo-900 hover:bg-gray-200 rounded"
              >
                Home
              </Link>
              <NavDropdown label="Donations" items={donationsItems} />
              <NavDropdown label="Communications" items={communicationsItems} />
              <NavDropdown label="Settings" items={settingsItems} />
              <button
                type="button"
                className="px-3 py-2 text-indigo-900 hover:bg-gray-200 rounded"
                onClick={async () => {
                  await Auth.signOut()
                  window.location.reload()
                }}
              >
                Sign Out
              </button>
            </>
          )}
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="w-full lg:hidden mt-2 pt-2 border-t border-gray-200">
            {isUserLoggedIn && (
              <div className="flex flex-col">
                <Link
                  to="/"
                  className="px-3 py-2 text-indigo-900 hover:bg-gray-200 rounded"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <NavDropdown label="Donations" items={donationsItems} isMobile />
                <NavDropdown label="Communications" items={communicationsItems} isMobile />
                <NavDropdown label="Settings" items={settingsItems} isMobile />
                <button
                  type="button"
                  className="px-3 py-2 text-left text-indigo-900 hover:bg-gray-200 rounded"
                  onClick={async () => {
                    await Auth.signOut()
                    window.location.reload()
                  }}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  )
}

export default NavBar
